import { useEffect, useState } from "react";
import { router, usePage } from "@inertiajs/react";
import { ChevronDown, Sprout, Tag, ArrowDownUp, X, Loader2, Newspaper, PencilLine } from "lucide-react";
import { ListTemplate } from "@/templates/ListTemplate";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { Menu } from "@/components/Menu";
import { Avatar } from "@/components/Avatar";
import { EmptyState } from "@/components/EmptyState";
import { StoryCard } from "@/feed/StoryCard";
import { Composer, type ComposerProject } from "@/feed/Composer";
import { type StoryPost, loadMorePosts } from "@/lib/feedApi";

interface Query {
  projects: string[];
  unassigned: boolean;
  topics: string[];
  from: string | null;
  to: string | null;
  sort: "asc" | "desc";
}

interface Props {
  posts: StoryPost[];
  hasMore: boolean;
  filters: {
    projects: { name: string; id: string }[];
    topics: { name: string; slug: string }[];
  };
  composer: { projects: ComposerProject[] };
  query: Query;
}

function buildQueryString(q: Query): string {
  const p = new URLSearchParams();
  const specific = q.projects.length > 0 || q.unassigned;
  if (specific) {
    p.set("filtered", "1");
    q.projects.forEach((id) => p.append("projects[]", id));
    if (q.unassigned) p.set("unassigned", "1");
  }
  q.topics.forEach((s) => p.append("topics[]", s));
  if (q.from) p.set("from", q.from);
  if (q.to) p.set("to", q.to);
  if (q.sort === "asc") p.set("sort", "asc");
  return p.toString();
}

export default function FeedIndex({ posts, hasMore, filters, composer, query }: Props) {
  const currentUser = (usePage().props as { currentUser?: { name?: string; avatarUrl?: string | null } }).currentUser;
  const [items, setItems] = useState(posts);
  const [more, setMore] = useState(hasMore);
  const [offset, setOffset] = useState(posts.length);
  const [loadingMore, setLoadingMore] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);

  useEffect(() => {
    setItems(posts);
    setMore(hasMore);
    setOffset(posts.length);
  }, [posts, hasMore]);

  function apply(next: Query) {
    const data: Record<string, string | string[]> = {};
    const specific = next.projects.length > 0 || next.unassigned;
    if (specific) {
      data.filtered = "1";
      data.projects = next.projects;
      if (next.unassigned) data.unassigned = "1";
    }
    if (next.topics.length) data.topics = next.topics;
    if (next.from) data.from = next.from;
    if (next.to) data.to = next.to;
    if (next.sort === "asc") data.sort = "asc";
    router.get("/feed", data, { preserveScroll: false });
  }

  async function onLoadMore() {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await loadMorePosts(offset, buildQueryString(query));
      setItems((cur) => [...cur, ...r.posts]);
      setOffset((o) => o + r.posts.length);
      setMore(r.hasMore);
    } finally {
      setLoadingMore(false);
    }
  }

  const toggleProject = (id: string) => {
    const has = query.projects.includes(id);
    apply({ ...query, projects: has ? query.projects.filter((p) => p !== id) : [...query.projects, id] });
  };
  const toggleTopic = (slug: string) => {
    const has = query.topics.includes(slug);
    apply({ ...query, topics: has ? query.topics.filter((t) => t !== slug) : [...query.topics, slug] });
  };

  const projectsLabel =
    query.projects.length === 0 && !query.unassigned
      ? "All projects"
      : `${query.projects.length + (query.unassigned ? 1 : 0)} selected`;
  const topicsLabel = query.topics.length === 0 ? "All topics" : `${query.topics.length} topic${query.topics.length > 1 ? "s" : ""}`;
  const anyFilter =
    query.projects.length > 0 || query.unassigned || query.topics.length > 0 || !!query.from || !!query.to || query.sort === "asc";

  return (
    <ListTemplate
      title="Feed"
      description="Impact updates and stories from the projects you supply."
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Feed" }]}
      filters={
        <>
          {filters.projects.length > 0 && (
            <Menu.Root>
              <Menu.Trigger asChild>
                <Button intent="secondary" size="md">
                  <Sprout /> {projectsLabel} <ChevronDown />
                </Button>
              </Menu.Trigger>
              <Menu.Content align="start" className="max-h-80 overflow-auto">
                <Menu.Label>Projects</Menu.Label>
                <Menu.CheckboxItem checked={query.unassigned} onCheckedChange={() => apply({ ...query, unassigned: !query.unassigned })}>
                  Unassigned
                </Menu.CheckboxItem>
                {filters.projects.map((p) => (
                  <Menu.CheckboxItem key={p.id} checked={query.projects.includes(p.id)} onCheckedChange={() => toggleProject(p.id)}>
                    {p.name}
                  </Menu.CheckboxItem>
                ))}
              </Menu.Content>
            </Menu.Root>
          )}

          {filters.topics.length > 0 && (
            <Menu.Root>
              <Menu.Trigger asChild>
                <Button intent="secondary" size="md">
                  <Tag /> {topicsLabel} <ChevronDown />
                </Button>
              </Menu.Trigger>
              <Menu.Content align="start" className="max-h-80 overflow-auto">
                <Menu.Label>Topics</Menu.Label>
                {filters.topics.map((t) => (
                  <Menu.CheckboxItem key={t.slug} checked={query.topics.includes(t.slug)} onCheckedChange={() => toggleTopic(t.slug)}>
                    {t.name}
                  </Menu.CheckboxItem>
                ))}
              </Menu.Content>
            </Menu.Root>
          )}

          <Menu.Root>
            <Menu.Trigger asChild>
              <Button intent="secondary" size="md">
                <ArrowDownUp /> {query.sort === "asc" ? "Oldest" : "Newest"} <ChevronDown />
              </Button>
            </Menu.Trigger>
            <Menu.Content align="start">
              <Menu.CheckboxItem checked={query.sort === "desc"} onCheckedChange={() => apply({ ...query, sort: "desc" })}>
                Newest first
              </Menu.CheckboxItem>
              <Menu.CheckboxItem checked={query.sort === "asc"} onCheckedChange={() => apply({ ...query, sort: "asc" })}>
                Oldest first
              </Menu.CheckboxItem>
            </Menu.Content>
          </Menu.Root>

          <div className="flex items-center gap-1.5">
            <Input
              type="date"
              aria-label="From date"
              value={query.from ?? ""}
              onChange={(e) => apply({ ...query, from: e.target.value || null })}
              className="w-[9.5rem]"
            />
            <span className="text-fg-subtle">–</span>
            <Input
              type="date"
              aria-label="To date"
              value={query.to ?? ""}
              onChange={(e) => apply({ ...query, to: e.target.value || null })}
              className="w-[9.5rem]"
            />
          </div>

          {anyFilter && (
            <Button
              intent="ghost"
              size="sm"
              onClick={() => apply({ projects: [], unassigned: false, topics: [], from: null, to: null, sort: "desc" })}
            >
              <X /> Clear
            </Button>
          )}
        </>
      }
    >
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
        <button
          type="button"
          onClick={() => setComposerOpen(true)}
          className="flex items-center gap-3 rounded-[var(--radius-lg)] border border-border-subtle bg-surface px-4 py-3 text-left shadow-[var(--shadow-1)] transition-colors hover:border-border-default"
        >
          <Avatar name={currentUser?.name || "You"} src={currentUser?.avatarUrl} size="md" />
          <span className="flex-1 text-sm text-fg-subtle">Share an update…</span>
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-accent-text [&_svg]:size-4">
            <PencilLine /> Post
          </span>
        </button>

        {items.length === 0 ? (
          <EmptyState
            icon={<Newspaper />}
            title="No posts yet"
            description="Share the first update for your projects."
          />
        ) : (
          <>
            {items.map((post) => (
              <StoryCard
                key={post.id}
                post={post}
                onDeleted={() => setItems((cur) => cur.filter((p) => p.id !== post.id))}
              />
            ))}

            {more && (
              <div className="flex justify-center py-2">
                <Button intent="secondary" onClick={onLoadMore} disabled={loadingMore}>
                  {loadingMore && <Loader2 className="animate-spin" />}
                  Load more
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <Composer
        open={composerOpen}
        projects={composer.projects}
        onClose={() => setComposerOpen(false)}
        onCreated={() => router.reload({ only: ["posts", "hasMore"] })}
      />
    </ListTemplate>
  );
}
